/**
 * Console Analyzer API types (mirror of core/modules/VantaMonitor/consoleAnalyzer)
 */
export const SEVERITIES = ['critical', 'high', 'medium', 'low', 'info'] as const;
export type Severity = typeof SEVERITIES[number];
export type SeverityCounts = Record<Severity, number>;
export type BootStatus = 'booting' | 'online' | 'stopped' | 'crashed' | 'ended';
export type MemoryKind = 'physical' | 'virtual';

export type CaBootSummary = {
    id: string;
    pid: string | null;
    status: BootStatus;
    isCurrent: boolean;
    startedAt: number;
    readyAt: number | null;
    endedAt: number | null;
    lastLineAt: number | null;
    bootDurationMs: number | null;
    lines: number;
    linesPerMin: number;
    resourcesFound: number | null;
    resourcesStarted: number;
    failedResources: string[];
    counts: SeverityCounts;
    totalFindings: number;
    assetsCount: number;
    droppedFindings: number;
};

export type CaLastBoot = {
    id: string;
    startedAt: number;
    status: BootStatus;
    counts: SeverityCounts;
    total: number;
    failed: number;
};

export type CaSummaryResp = {
    ts: number;
    boot: CaBootSummary | null;
    lastBoots: CaLastBoot[];
    tabs: { findings: number; resources: number; assets: number; boots: number };
};

export type CaFinding = {
    key: string;
    rule: string;
    title: string;
    severity: Severity;
    resource: string | null;
    message: string;
    count: number;
    firstSeen: number;
    lastSeen: number;
    phase: 'boot' | 'runtime';
    value?: number;
};

export type CaFindingsResp = {
    bootId: string;
    total: number;
    offset: number;
    limit: number;
    resources: string[];
    items: CaFinding[];
};

export type CaResourceRow = {
    resource: string;
    counts: SeverityCounts;
    worst: Severity | null;
    findings: number;
    occurrences: number;
    assets: number;
    failed: boolean;
    lastSeen: number;
};

export type CaResourcesResp = {
    bootId: string;
    items: CaResourceRow[];
};

export type CaAsset = {
    resource: string;
    file: string;
    ext: string;
    sizeMiB: number;
    memory: MemoryKind;
    severity: Severity;
    count: number;
    firstSeen: number;
    lastSeen: number;
};

export type CaAssetResourceRow = {
    resource: string;
    worst: Severity;
    assets: number;
    physicalMiB: number;
    virtualMiB: number;
    totalMiB: number;
    largest: CaAsset;
};

export type CaAssetsResp = {
    bootId: string;
    thresholds: { severity: Severity; minMiB: number }[];
    stats: {
        count: number;
        totalMiB: number;
        resources: number;
        largest: CaAsset | null;
        counts: SeverityCounts;
    };
    perResource: CaAssetResourceRow[];
    total: number;
    offset: number;
    limit: number;
    items: CaAsset[];
};

export type CaBootsResp = {
    currentId: string | null;
    boots: CaBootSummary[];
};

export type CaErrorResp = { error: string };
